import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  InputAdornment,
  IconButton,
  Checkbox
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

// ==================== INDEPENDENT AMOUNT INPUT ====================
const IndependentAmountInput = memo(({ initialValue, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(initialValue || "");
    }
    isInternalChange.current = false;
  }, [initialValue]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*\.?\d{0,2}$/.test(newValue) && newValue.length <= 10) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      onWheel={(e) => e.target.blur()}
      placeholder="Enter amount limit"
      size="small"
      disabled={disabled}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <span style={{ color: '#666', fontWeight: 500 }}>₹</span>
          </InputAdornment>
        ),
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{
        mb: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
          '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
        }
      }}
    />
  );
});

// ==================== INDEPENDENT TRANSACTION INPUT ====================
const IndependentTransactionInput = memo(({ initialValue, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(initialValue || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(initialValue || "");
    }
    isInternalChange.current = false;
  }, [initialValue]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*$/.test(newValue) && newValue.length <= 6) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      onWheel={(e) => e.target.blur()}
      placeholder="Enter transaction count"
      size="small"
      disabled={disabled}
      InputProps={{
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{
        mb: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
          '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
        }
      }}
    />
  );
});

// ==================== DURATION NUMBER INPUT ====================
const DurationInput = memo(({ value, onChange, unit, disabled }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      return;
    }
    
    if (/^\d*$/.test(newValue)) {
      let numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue > 0) {
        setLocalValue(numValue);
        
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onChange(numValue);
        }, 300);
      } else if (newValue === "0") {
        setLocalValue(1);
        onChange(1);
      }
    }
  };

  const handleBlur = () => {
    let numValue = parseInt(localValue, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setLocalValue(1);
      onChange(1);
    } else {
      onChange(numValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextField
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target.blur()}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
          }
        }}
        size="small"
        disabled={disabled}
        inputProps={{
          min: 1,
          style: { textAlign: 'center' }
        }}
        sx={{
          width: 100,
          '& input[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            }
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
            '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
          }
        }}
      />
      <Typography variant="caption" sx={{ color: "#666", minWidth: 40 }}>
        {unit}
      </Typography>
    </Box>
  );
});

// ==================== FREQUENCY BUTTONS ====================
const FrequencyButtons = memo(({ value, onChange, options, disabled }) => {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {options.map((option) => (
        <Button
          key={option.value}
          size="small"
          variant={value === option.value ? "contained" : "outlined"}
          onClick={() => onChange(option.value)}
          disabled={disabled}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: "12px",
            ...(value === option.value ? {
              background: "#14AE78",
              "&:hover": { background: "#0f8c65" }
            } : {})
          }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
});

// ==================== AMOUNT SECTION ====================
const AmountSection = memo(({ 
  initialValue,
  initialFrequency,
  initialDuration,
  onDataChange,
  loading,
  title,
  color
}) => {
  const [value, setValue] = useState(initialValue || "");
  const [frequency, setFrequency] = useState(initialFrequency || "DAYS");
  const [duration, setDuration] = useState(initialDuration || 1);
  
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  const sendDataToParent = useCallback((newValue, newFrequency, newDuration) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (newValue && parseFloat(newValue) > 0) {
        onDataChange({
          limitOn: "AMOUNT",
          value: parseFloat(newValue),
          frequency: newFrequency,
          duration: newDuration
        });
      }
    }, 300);
  }, [onDataChange]);

  const handleValueChange = useCallback((newValue) => {
    setValue(newValue);
    sendDataToParent(newValue, frequency, duration);
  }, [frequency, duration, sendDataToParent]);

  const handleFrequencyChange = useCallback((newFrequency) => {
    setFrequency(newFrequency);
    setDuration(1);
    sendDataToParent(value, newFrequency, 1);
  }, [value, sendDataToParent]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    sendDataToParent(value, frequency, newDuration);
  }, [value, frequency, sendDataToParent]);

  useEffect(() => {
    if (!isInitialized.current && initialValue) {
      setValue(initialValue);
      let uiFrequency = initialFrequency;
      if (uiFrequency === "MONTH") uiFrequency = "MONTHS";
      setFrequency(uiFrequency || "DAYS");
      setDuration(initialDuration || 1);
      isInitialized.current = true;
    }
  }, [initialValue, initialFrequency, initialDuration]);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: color || "#2e7d32", mb: 2 }}>
        {title || "Amount Limit"}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
            Limit Value
          </Typography>
          <Typography variant="caption" sx={{ color: "#999" }}>
            {value.length}/10
          </Typography>
        </Box>
        <IndependentAmountInput
          initialValue={value}
          onValueChange={handleValueChange}
          disabled={loading}
        />
        <Typography variant="caption" sx={{ color: "#666" }}>
          Maximum amount (in ₹) per period
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Frequency
        </Typography>
        <FrequencyButtons
          value={frequency}
          onChange={handleFrequencyChange}
          options={frequencyOptions}
          disabled={loading}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Duration
        </Typography>
        <DurationInput
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
          unit={unit}
        />
      </Box>
    </Box>
  );
});

// ==================== TRANSACTION SECTION ====================
const TransactionSection = memo(({ 
  initialValue,
  initialFrequency,
  initialDuration,
  onDataChange,
  loading,
  title,
  color
}) => {
  const [value, setValue] = useState(initialValue || "");
  const [frequency, setFrequency] = useState(initialFrequency || "DAYS");
  const [duration, setDuration] = useState(initialDuration || 1);
  
  const isInitialized = useRef(false);
  const timeoutRef = useRef(null);

  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  const sendDataToParent = useCallback((newValue, newFrequency, newDuration) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (newValue && parseFloat(newValue) > 0) {
        onDataChange({
          limitOn: "TRANSACTION",
          value: parseInt(newValue, 10),
          frequency: newFrequency,
          duration: newDuration
        });
      }
    }, 300);
  }, [onDataChange]);

  const handleValueChange = useCallback((newValue) => {
    setValue(newValue);
    sendDataToParent(newValue, frequency, duration);
  }, [frequency, duration, sendDataToParent]);

  const handleFrequencyChange = useCallback((newFrequency) => {
    setFrequency(newFrequency);
    setDuration(1);
    sendDataToParent(value, newFrequency, 1);
  }, [value, sendDataToParent]);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration(newDuration);
    sendDataToParent(value, frequency, newDuration);
  }, [value, frequency, sendDataToParent]);

  useEffect(() => {
    if (!isInitialized.current && initialValue) {
      setValue(initialValue);
      let uiFrequency = initialFrequency;
      if (uiFrequency === "MONTH") uiFrequency = "MONTHS";
      setFrequency(uiFrequency || "DAYS");
      setDuration(initialDuration || 1);
      isInitialized.current = true;
    }
  }, [initialValue, initialFrequency, initialDuration]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: color || "#1565c0", mb: 2 }}>
        {title || "Transaction Limit"}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
            Transaction Count
          </Typography>
          <Typography variant="caption" sx={{ color: "#999" }}>
            {value.length}/6
          </Typography>
        </Box>
        <IndependentTransactionInput
          initialValue={value}
          onValueChange={handleValueChange}
          disabled={loading}
        />
        <Typography variant="caption" sx={{ color: "#666" }}>
          Maximum number of transactions per period
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Frequency
        </Typography>
        <FrequencyButtons
          value={frequency}
          onChange={handleFrequencyChange}
          options={frequencyOptions}
          disabled={loading}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Duration
        </Typography>
        <DurationInput
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
          unit={unit}
        />
      </Box>
    </Box>
  );
});

// ==================== MAIN COMPONENT ====================
const UsageLimitComponent = ({
  usageLimitRequired,
  handleUsageLimitChange,
  onLimitsChange,
  initialLimits = {},
  loading,
}) => {
  // Checkbox states
  const [enableBP, setEnableBP] = useState(false);
  const [enableTagPay, setEnableTagPay] = useState(false);
  
  // BP Limits
  const [bpAmount, setBpAmount] = useState(null);
  const [bpTransaction, setBpTransaction] = useState(null);
  
  // Tag Limits
  const [tagAmount, setTagAmount] = useState(null);
  const [tagTransaction, setTagTransaction] = useState(null);

  // Initialize from existing data
  useEffect(() => {
    if (initialLimits && initialLimits.accountTypes) {
      const accountTypes = initialLimits.accountTypes || [];
      setEnableBP(accountTypes.includes("WALLET"));
      setEnableTagPay(accountTypes.includes("WALLET_RFID"));
      
      if (initialLimits.bpLimits) {
        const amountLimit = initialLimits.bpLimits.find(l => l.limitOn === "AMOUNT");
        const transactionLimit = initialLimits.bpLimits.find(l => l.limitOn === "TRANSACTION");
        if (amountLimit) setBpAmount(amountLimit);
        if (transactionLimit) setBpTransaction(transactionLimit);
      }
      
      if (initialLimits.tagLimits) {
        const amountLimit = initialLimits.tagLimits.find(l => l.limitOn === "AMOUNT");
        const transactionLimit = initialLimits.tagLimits.find(l => l.limitOn === "TRANSACTION");
        if (amountLimit) setTagAmount(amountLimit);
        if (transactionLimit) setTagTransaction(transactionLimit);
      }
    }
  }, [initialLimits]);

  const sendCombinedData = useCallback(() => {
    const accountTypes = [];
    const bpLimits = [];
    const tagLimits = [];
    
    if (enableBP) {
      accountTypes.push("WALLET");
      if (bpAmount && bpAmount.value && bpAmount.value > 0) {
        bpLimits.push(bpAmount);
      }
      if (bpTransaction && bpTransaction.value && bpTransaction.value > 0) {
        bpLimits.push(bpTransaction);
      }
    }
    
    if (enableTagPay) {
      accountTypes.push("WALLET_RFID");
      if (tagAmount && tagAmount.value && tagAmount.value > 0) {
        tagLimits.push(tagAmount);
      }
      if (tagTransaction && tagTransaction.value && tagTransaction.value > 0) {
        tagLimits.push(tagTransaction);
      }
    }
    
    const usageLimitData = {
      usageLimitRequired: usageLimitRequired === "Yes",
      accountTypes: accountTypes,
    };
    
    if (bpLimits.length > 0) {
      usageLimitData.bpLimits = bpLimits;
    }
    
    if (tagLimits.length > 0) {
      usageLimitData.tagLimits = tagLimits;
    }
    
    if (onLimitsChange) {
      onLimitsChange(usageLimitData);
    }
  }, [enableBP, enableTagPay, bpAmount, bpTransaction, tagAmount, tagTransaction, usageLimitRequired, onLimitsChange]);

  useEffect(() => {
    if (usageLimitRequired === "Yes") {
      sendCombinedData();
    }
  }, [enableBP, enableTagPay, bpAmount, bpTransaction, tagAmount, tagTransaction, sendCombinedData, usageLimitRequired]);

  const handleBPChange = (e) => {
    setEnableBP(e.target.checked);
    if (!e.target.checked) {
      setBpAmount(null);
      setBpTransaction(null);
    }
  };

  const handleTagPayChange = (e) => {
    setEnableTagPay(e.target.checked);
    if (!e.target.checked) {
      setTagAmount(null);
      setTagTransaction(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, margin: "auto" }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
        Usage Limit
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
        Define spending thresholds and transaction volume controls for this corporate account.
      </Typography>

      <Box sx={{ background: "#f8f9fa", borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
          Do you want to set a usage limit?
        </Typography>

        <RadioGroup
          value={usageLimitRequired}
          onChange={(e) => handleUsageLimitChange(e.target.value)}
          row
          sx={{ gap: 3 }}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Yes</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  Set a usage limit for corporate
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "Yes" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
          <FormControlLabel
            value="No"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>No</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  No usage limit required
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "No" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
        </RadioGroup>
      </Box>

      {usageLimitRequired === "Yes" && (
        <Box sx={{ background: "white", borderRadius: 2, p: 3, border: "1px solid #e0e0e0", mt: 2 }}>
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
            Select Account Type
          </Typography>
          
          <Box sx={{ display: "flex", gap: 4, mb: 4 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={enableBP}
                  onChange={handleBPChange}
                  sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }}
                />
              }
              label="Business Payment (BP)"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={enableTagPay}
                  onChange={handleTagPayChange}
                  sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }}
                />
              }
              label="TagPay"
            />
          </Box>

          {/* BP Limits */}
          {enableBP && (
            <>
              <AmountSection
                initialValue={bpAmount?.value?.toString() || ""}
                initialFrequency={bpAmount?.frequency || "DAYS"}
                initialDuration={bpAmount?.duration || 1}
                onDataChange={setBpAmount}
                loading={loading}
                title="BP Amount Limit"
                color="#2e7d32"
              />
              <TransactionSection
                initialValue={bpTransaction?.value?.toString() || ""}
                initialFrequency={bpTransaction?.frequency || "DAYS"}
                initialDuration={bpTransaction?.duration || 1}
                onDataChange={setBpTransaction}
                loading={loading}
                title="BP Transaction Limit"
                color="#1565c0"
              />
            </>
          )}

          {/* TagPay Limits */}
          {enableTagPay && (
            <>
              <AmountSection
                initialValue={tagAmount?.value?.toString() || ""}
                initialFrequency={tagAmount?.frequency || "DAYS"}
                initialDuration={tagAmount?.duration || 1}
                onDataChange={setTagAmount}
                loading={loading}
                title="TagPay Amount Limit"
                color="#2e7d32"
              />
              <TransactionSection
                initialValue={tagTransaction?.value?.toString() || ""}
                initialFrequency={tagTransaction?.frequency || "DAYS"}
                initialDuration={tagTransaction?.duration || 1}
                onDataChange={setTagTransaction}
                loading={loading}
                title="TagPay Transaction Limit"
                color="#1565c0"
              />
            </>
          )}

          {!enableBP && !enableTagPay && (
            <Typography variant="body2" sx={{ color: "#999", textAlign: "center", py: 4 }}>
              Please select at least one account type
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UsageLimitComponent;